import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "../ui/input";

// Plain text input with as-you-type suggestions. Typed text is the value; options: [{ name, meta?, icon? }].
const TypeAheadInput = ({ value, onChange, options, id, testId, placeholder, optionTestId = (o) => `option-${o.name}` }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  const q = (value || "").trim().toLowerCase();
  const matches = useMemo(
    () => options.filter((o) => (!q || o.name.toLowerCase().includes(q)) && o.name.toLowerCase() !== q).slice(0, 12),
    [options, q]
  );

  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (name) => { onChange(name); setOpen(false); };
  const onKeyDown = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % matches.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + matches.length) % matches.length); }
    else if (e.key === "Enter") { e.preventDefault(); pick(matches[active].name); }
    else if (e.key === "Escape" || e.key === "Tab") setOpen(false);
  };

  const show = open && matches.length > 0;
  return (
    <div ref={wrapRef} className="relative">
      <Input
        id={id}
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={show}
        data-testid={testId}
      />
      {show && (
        <ul className="absolute z-[70] mt-1 w-full max-h-64 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md py-1 text-sm" role="listbox" data-testid={`${testId}-suggestions`}>
          {matches.map((o, i) => (
            <li
              key={`${o.meta || ""}-${o.name}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(o.name); }}
              onMouseEnter={() => setActive(i)}
              className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer ${i === active ? "bg-accent text-accent-foreground" : ""}`}
              data-testid={optionTestId(o)}
            >
              {o.icon}
              <span className="truncate">{o.name}</span>
              {o.meta && <span className="ml-auto text-[11px] text-muted-foreground">{o.meta}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TypeAheadInput;
